import { debugError } from '@utils';

class AddToCartData {
  /**
   * Create add to cart data for the configurator
   * Channels and campaigns are handled at cart level
   * @param {Object} variant
   * @param {Object} sku
   * @param {number} quantity
   * @param {string} configurationId
   * @param {string} previousConfigurationId
   * @param {string} uploadKey
   * @param {string} thumbnail
   * @returns {Object}
   */
  create({
    variant,
    sku,
    quantity = 1,
    configurationId,
    previousConfigurationId,
    uploadKey,
    thumbnail,
    imageEffect,
  }) {
    if (!variant || !sku) return null;

    try {
      // Transform gid to a id
      const id = variant?.id?.split('/').pop();

      // Format the thumbnail
      const thumbnailUrl = this.#formatThumbnailUrl(thumbnail);

      // Get the add-ons
      // Format as SKU's for Liquid and Shopify Functions usage
      // Format as Handles for storefrontAPI and UI Extension usage
      const addOns = this.#getAddOns(sku, variant.addOns);
      const addOnSkus = addOns?.map((item) => item.sku)?.join(',');
      const addOnHandles = addOns?.map((item) => item.handle)?.join(',');

      // Get options
      // Format as SKU's for Liquid and Shopify Functions usage
      // Format as Handles for storefrontAPI and UI Extension usage
      const options = this.#getOptions(sku, variant.options);
      const optionSkus = options?.map((item) => item.sku)?.join(',');
      const optionHandles = options?.map((item) => item.handle)?.join(',');

      // Calculate the original price per unit
      const originalPricePerUnit = this.#calculateOriginalPricePerUnit(
        variant.original_price,
        addOns?.map((item) => item.sku)
      );

      // Create the properties
      const properties = {
        // Picturator import path for this line item. Everything the upload widget
        // configures is a single-image design that lives in Picturator's
        // `configureditems` table, so the importer must resolve it from
        // `_configuration_id`. Stated explicitly rather than left to a default: the
        // importer branches on the value, and a missing field would be ambiguous with
        // universal-configurator items. NGE-10195 / NGE-10122.
        _tcg_source: 'single-configurator',
        _sku: sku._,
        _PM: sku.PM,
        _F: sku.F,
        _upload_key: uploadKey,
        _original_price_per_unit: originalPricePerUnit,
        _configuration_id: configurationId,
        _previous_configuration_id: previousConfigurationId,
        _thumbnail: thumbnailUrl,
        ...(imageEffect && { _image_effect: imageEffect }),
        ...(addOns &&
          addOns?.length > 0 && {
            _add_ons: addOnSkus,
            _add_on_handles: addOnHandles,
            _add_ons_display: this.#generateDisplayData(addOns),
          }),
        ...(options &&
          options?.length > 0 && {
            _options: optionSkus,
            _option_handles: optionHandles,
            _options_display: this.#generateDisplayData(options),
          }),
      };

      // Create the form data
      return {
        attributes: {
          _market: window.theme.market || '',
          _brand_id: window.theme.brandId || '',
          _language: window.theme.language || '',
          _market_groups: window.theme.market_group.index,
        },
        items: [
          {
            id,
            quantity,
            properties,
          },
        ],
      };
    } catch (error) {
      debugError('Error creating add to cart data', error);
      throw new Error(window.toast_translations.cart.no_product);
    }
  }

  /**
   * Format the thumbnail URL
   * @param {string} thumbnail - The thumbnail URL
   * @returns {string} - The formatted thumbnail URL
   */
  #formatThumbnailUrl(thumbnail) {
    if (!thumbnail) return null;
    if (thumbnail.startsWith('data:')) return null;

    // Remove the size parameter from the thumbnail URL
    const thumbnailUrl = new URL(thumbnail);
    thumbnailUrl.searchParams.delete('size');
    return thumbnailUrl.toString();
  }

  /**
   * Get the add-ons
   * @param {Object} sku - The sku object from the configurator
   * @param {Object} addOns - The add-ons of the variant
   * @returns {Object} - The add-ons
   */
  #getAddOns(sku, addOns) {
    if (!sku || !addOns) return null;

    // Transform all sku keys and values to uppercase
    const skuData = Object.fromEntries(
      Object.entries(sku).map(([key, value]) => [key.toUpperCase(), value?.toUpperCase()])
    );

    // Join the SKU array
    const items = addOns.filter((item) => {
      const sku = item.sku?.toUpperCase();
      const type = item.type?.toUpperCase();
      if (sku && type && skuData[type] && skuData[type] === sku) {
        return true;
      }
      return false;
    });

    // Return the add-ons
    return items;
  }

  /**
   * Get the options
   * @param {Object} sku - The sku object from the configurator
   * @param {Object} options - The options of the variant
   * @returns {Object} - The options
   */
  #getOptions(sku, options) {
    if (!sku || !options) return null;

    // Transform all sku keys and values to uppercase
    const skuData = Object.fromEntries(
      Object.entries(sku).map(([key, value]) => [key.toUpperCase(), value?.toUpperCase()])
    );

    const items = options.filter((item) => {
      const sku = item.sku?.toUpperCase();
      const type = item.type?.toUpperCase();
      if (sku && type && skuData[type] && skuData[type] === sku) {
        return true;
      }
      return false;
    });

    // Return the options
    return items;
  }

  /**
   * Generate display data for add-ons/options - stores type and title for email templates
   * @param {Array} items - The add-ons or options array
   * @returns {string} - Formatted string like "BM:Canvas Backing|MT:Wall Mount"
   */
  #generateDisplayData(items) {
    if (!items || items.length === 0) return '';

    return items
      .map(
        (item) =>
          // Store type and display_title as structured data
          `${item.type}:${item.display_title}`
      )
      .join('|'); // Use | as separator between items, : as separator within items
  }

  /**
   * Calculate the price per unit
   * @param {number} price - The price of the variant
   * @param {number} compareAtPrice - The compare at price of the variant
   * @param {number} originalPrice - The original price of the variant
   * @param {Array} addOns - The add-on sku's of the variant
   * @returns {number} - The price per unit
   */

  /**
   * Calculate the original price per unit
   * @param {number} priceData - The original price json data of the variant
   * @param {Array} addOns - The add-on sku's of the variant
   * @returns {Array} - The original price per unit
   */
  #calculateOriginalPricePerUnit(priceData, addOns) {
    // Use the default or compare at price from Shopify
    const originalPricePerUnit = [];

    // Get all market codes from the market group index
    const marketGroupIndex = window.theme.market_group.index;

    // Check if we have a market group index
    if (!marketGroupIndex) {
      return originalPricePerUnit;
    }

    // Check if we have a price data
    if (!priceData || !priceData._) {
      return originalPricePerUnit;
    }

    // Calculate price for each market
    Object.keys(marketGroupIndex).forEach((group) => {
      // Skip the default market
      if (group === '_v') return;
      if (group === '_p') return;

      const marketCode = group.toUpperCase();
      const marketCodeDowncase = marketCode.toLowerCase();
      let marketPrice = priceData._[marketCode] || priceData._[marketCodeDowncase] || 0;

      // Add prices for add-ons
      if (addOns && addOns?.length > 0) {
        addOns.forEach((addOn) => {
          // Case-insensitive on the ADD-ON key, not just the market code. The strike map is
          // authored data and the metaobject `sku` is authored separately, so their casing
          // is not guaranteed to agree: an exact-key lookup silently dropped an add-on's
          // strike whenever they differed, and the line understated its saving. The UC
          // bridge (tcg-configurator.init.js, computeOriginalPricePerUnit) already resolves
          // all three spellings — both paths feed the SAME checkout extension, so a canvas
          // line and a UC line in one order summary must not disagree (@davis-abols,
          // PR #190). NGE-11736.
          const entry =
            priceData[addOn] ||
            priceData[String(addOn).toUpperCase()] ||
            priceData[String(addOn).toLowerCase()];
          if (entry) {
            marketPrice += entry[marketCode] || entry[marketCodeDowncase] || 0;
          }
        });
      }

      // Add the price to the array
      originalPricePerUnit.push({
        mg: marketCode,
        p: marketPrice,
      });
    });

    // Return the original price per unit
    return originalPricePerUnit;
  }
}

const addToCartData = new AddToCartData();
export { addToCartData };
